import React from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { Doughnut } from "react-chartjs-2";
import { MoreVertical } from "react-feather";

import { orange, green, red } from "@mui/material/colors";
import {
  Card as MuiCard,
  CardContent,
  CardHeader,
  IconButton,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableHead,
  TableRow as MuiTableRow,
  Typography,
} from "@mui/material";
import { spacing } from "@mui/system";

const Card = styled(MuiCard)(spacing);

const ChartWrapper = styled.div`
  height: 168px;
  position: relative;
`;

const DoughnutInner = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -22px;
  text-align: center;
  z-index: 0;
`;

const TableRow = styled(MuiTableRow)`
  height: 42px;
`;

const TableCell = styled(MuiTableCell)`
  padding-top: 0;
  padding-bottom: 0;
`;

const GreenText = styled.span`
  color: ${() => green[400]};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const RedText = styled.span`
  color: ${() => red[400]};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const DoughnutChart = ({ theme, summary, dateLabel }) => {
  const items = Number(summary?.items) || 0;
  const failures = Number(summary?.failures) || 0;
  const passes = Math.max(items - failures, 0);
  const passRate = items > 0 ? passes / items : 0;

  const data = {
    labels: ["Pass", "Fail"],
    datasets: [
      {
        data: [passes, failures],
        backgroundColor: [green[500], red[500]],
        borderWidth: 5,
        borderColor: theme.palette.background.paper,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    cutout: "80%",
  };

  return (
    <Card mb={6}>
      <CardHeader
        action={
          <IconButton aria-label="settings" size="large">
            <MoreVertical />
          </IconButton>
        }
        title={`Pass / Fail (${dateLabel || "Latest"})`}
      />

      <CardContent>
        <ChartWrapper>
          <DoughnutInner>
            <Typography variant="h4">
              {`${Math.round(passRate * 100)}%`}
            </Typography>
            <Typography variant="caption">pass rate</Typography>
          </DoughnutInner>
          <Doughnut data={data} options={options} />
        </ChartWrapper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Metric</TableCell>
              <TableCell align="right">Count</TableCell>
              <TableCell align="right">Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row">
                Items Tested
              </TableCell>
              <TableCell align="right">{items}</TableCell>
              <TableCell align="right">—</TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Passes
              </TableCell>
              <TableCell align="right">{passes}</TableCell>
              <TableCell align="right">
                <GreenText>{`${Math.round(passRate * 100)}%`}</GreenText>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Failures
              </TableCell>
              <TableCell align="right">{failures}</TableCell>
              <TableCell align="right">
                {failures > 0 ? (
                  <RedText>check table →</RedText>
                ) : (
                  <GreenText>none</GreenText>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row">
                Tests Run
              </TableCell>
              <TableCell align="right">{Number(summary?.tests) || 0}</TableCell>
              <TableCell align="right">—</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default withTheme(DoughnutChart);
